import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import store from "store";
import { useAppDispatch, useAppSelector } from "../Hooks/redux";
import { loginSuccess, meRequest, selectUser } from "../Redux/AuthRedux";
import Api from "../Services/Api";
import AppHeader from "./AppHeader";
import ExperimentCard from "./ExperimentCard";
import ExperimentsByTrack from "./ExperimentsByTrack";
import sampleExperiment from "./sampleExperiment";

const api = Api.create();

/**
 * Compact teaser control — lives in the sticky payment panel so the
 * "Show me points for free" CTA is next to the "Pay" button. Insight
 * cards (when ready) render in the main column via <TeaserInsights />.
 */
function TeaserPanel({ selectedSite, formatSiteLabel, teaser, onShowPoints }) {
  const state = teaser?.state || "idle";
  const siteLabel = selectedSite ? formatSiteLabel(selectedSite) : "your picked site";

  if (state === "ready") {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-emerald-800">
          <span className="text-base">✓</span>
          <div className="text-sm font-bold">Free preview ready</div>
        </div>
        <p className="text-xs text-emerald-900 mt-1 mb-3">
          {(teaser?.data?.experiments?.length || teaser?.data?.insights?.length) || 0} real opportunities on your site — see them below.
        </p>
        <a
          href="#teaser-insights"
          className="block text-center w-full px-3 py-2 rounded-lg bg-white border border-emerald-300 text-emerald-800 text-xs font-bold hover:bg-emerald-100 cursor-pointer"
        >
          Jump to insights ↓
        </a>
      </div>
    );
  }

  if (state === "running") {
    const STAGES = [
      { key: "fetching_gsc", label: "Pulling 90 days of Search Console data" },
      { key: "analyzing",    label: "Scanning queries, CTR gaps & page-2 wins" },
      { key: "picking",      label: "Picking your biggest opportunities" },
    ];
    const currentIdx = Math.max(0, STAGES.findIndex((s) => s.key === teaser?.stage));
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-block w-3.5 h-3.5 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
          <div className="text-sm font-bold text-slate-900">Generating your preview…</div>
        </div>
        <ul className="space-y-1.5">
          {STAGES.map((s, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            return (
              <li key={s.key} className="flex items-start gap-2 text-xs">
                <span
                  className={
                    "mt-[3px] inline-block w-3 h-3 rounded-full flex-shrink-0 " +
                    (done ? "bg-emerald-500" : active ? "bg-indigo-500 animate-pulse" : "bg-slate-200")
                  }
                />
                <span className={done ? "text-slate-500 line-through" : active ? "text-slate-900 font-semibold" : "text-slate-400"}>
                  {s.label}
                </span>
              </li>
            );
          })}
        </ul>
        <p className="text-[11px] text-slate-400 mt-3">Usually takes 15–30 seconds.</p>
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="text-sm font-bold text-red-800 mb-1">Preview failed</div>
        <div className="text-xs text-red-700 mb-3">{teaser?.error || "Please try again."}</div>
        <button
          onClick={onShowPoints}
          disabled={!selectedSite}
          className="w-full px-3 py-2 rounded-lg bg-teal-600 text-white text-xs font-bold hover:bg-teal-700 disabled:opacity-60 cursor-pointer"
        >
          Retry free preview
        </button>
      </div>
    );
  }

  // idle
  return (
    <div className="bg-gradient-to-br from-teal-50 to-indigo-50 border border-teal-200 rounded-xl p-4">
      <div className="text-[11px] font-bold text-teal-700 uppercase tracking-wide mb-1">Free preview</div>
      <div className="text-sm font-extrabold text-slate-900 mb-1">See 5–10 real points from your own data</div>
      <p className="text-xs text-slate-600 mb-3">
        No payment yet. We'll run a quick scan on <span className="font-semibold break-all">{siteLabel}</span> and show the biggest opportunities we find — then you decide.
      </p>
      <button
        onClick={onShowPoints}
        disabled={!selectedSite}
        className="w-full px-3 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-extrabold hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
      >
        Show me points for free →
      </button>
    </div>
  );
}

/**
 * Teaser insights — renders the SAME ExperimentCard the paid report uses so
 * users see exactly what they're buying (full checklist, Claude prompt,
 * copy-for-Cursor, proposed title/meta, target queries). Just fewer cards.
 *
 * Accepts both the new `experiments` shape and the legacy `insights` shape
 * so existing rows in the DB don't break before the job regenerates.
 */
function TeaserInsights({ teaser }) {
  const experiments = teaser?.data?.experiments || teaser?.data?.insights || [];
  const total = teaser?.data?.total;
  const stillGenerating = typeof total === "number" && experiments.length < total;

  // Build a lightweight progress object so ExperimentsByTrack can show the
  // same "Generating…" state it does on the paid report page.
  const progress = {};
  for (const exp of experiments) {
    const pk = {
      "ctr-fix": "ctr_fix",
      "new-page": "new_pages",
      "content-refresh": "content_refresh",
    }[exp.track];
    if (pk) progress[pk] = stillGenerating ? "running" : "done";
  }

  return (
    <div className="mb-8" id="teaser-insights">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-xs font-bold text-teal-700 uppercase tracking-wide">Your free preview</div>
          <div className="text-base font-extrabold text-slate-900">
            {experiments.length}{total ? ` of ${total}` : ""} full experiments from {teaser?.site_url}
          </div>
        </div>
        <span className="text-[11px] text-slate-400">
          {stillGenerating ? "Generating more…" : "Same depth as the paid report — just fewer"}
        </span>
      </div>

      <ExperimentsByTrack
        experiments={experiments}
        progress={progress}
        isProcessing={stillGenerating}
        showSummaryBar={false}
      />

      <div className="mt-6 bg-gradient-to-br from-indigo-50 to-teal-50 border border-indigo-200 rounded-xl p-5 text-center">
        <div className="text-sm font-extrabold text-slate-900 mb-1">
          150+ more like these in the full report
        </div>
        <p className="text-xs text-slate-600">
          Across the complete 90 days of your GSC data — every CTR fix, every new-page opportunity, every content refresh target. Same format, same depth.
        </p>
      </div>
    </div>
  );
}

/**
 * /connected — post-Google-OAuth confirmation + Search Console site picker.
 *
 * The Rails OAuth callback redirects here with `?token=<jwt>`. We stash the
 * JWT, fetch the user, then fetch the list of GSC properties the user has
 * access to so they can pick which one to scan before paying.
 */
export default function ConnectedPage() {
  const [params] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);

  const [authReady, setAuthReady] = useState(false);
  const [sites, setSites] = useState([]);
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState("");
  const [selectedSite, setSelectedSite] = useState("");

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  const [teaser, setTeaser] = useState(null);
  const teaserPoll = useRef(null);

  // Hydrate auth state from ?token=... (fallback to localStorage on reload)
  useEffect(() => {
    const token = params.get("token") || store.get("token");
    if (!token) {
      navigate("/start", { replace: true });
      return;
    }
    store.set("token", token);
    api.setAuthToken(token);
    dispatch(loginSuccess({ user: null, token }));
    dispatch(meRequest({ callback: () => {} }));
    if (params.get("token")) {
      // Strip ?token=... but keep whichever path the user is on (/ or /connected).
      window.history.replaceState({}, "", window.location.pathname);
    }
    setAuthReady(true);
  }, [params, dispatch, navigate]);

  useEffect(() => {
    if (!authReady) return;
    setSitesLoading(true);
    setSitesError("");
    api.listGoogleSites().then((resp) => {
      setSitesLoading(false);
      setSitesLoaded(true);
      if (resp.ok && Array.isArray(resp.data?.sites)) {
        setSites(resp.data.sites);
      } else {
        setSitesError(resp.data?.error || "Could not load your Search Console sites.");
      }
    });
  }, [authReady]);

  const formatSiteLabel = (siteUrl) => {
    if (siteUrl.startsWith("sc-domain:")) {
      return `${siteUrl.replace("sc-domain:", "")} (domain property)`;
    }
    return siteUrl;
  };

  // Sorted alphabetically by the display label so the dropdown is predictable.
  const sortedSites = useMemo(() => {
    return [...sites].sort((a, b) =>
      formatSiteLabel(a.site_url).localeCompare(formatSiteLabel(b.site_url), undefined, { sensitivity: "base" })
    );
  }, [sites]);

  // Auto-select the only site or restore the first sorted one if none chosen.
  useEffect(() => {
    if (!selectedSite && sortedSites.length > 0) {
      setSelectedSite(sortedSites[0].site_url);
    }
  }, [sortedSites, selectedSite]);

  const permissionBadge = (level) => {
    const map = {
      siteOwner: { label: "Owner", cls: "bg-emerald-50 text-emerald-700" },
      siteFullUser: { label: "Full", cls: "bg-emerald-50 text-emerald-700" },
      siteRestrictedUser: { label: "Restricted", cls: "bg-amber-50 text-amber-700" },
    };
    const v = map[level] || { label: level, cls: "bg-slate-100 text-slate-600" };
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${v.cls}`}>{v.label}</span>;
  };

  // Prime the teaser state from the backend on load so a prior teaser shows.
  useEffect(() => {
    if (!authReady) return;
    api.getTeaser().then((resp) => {
      if (resp.ok) setTeaser(resp.data);
    });
  }, [authReady]);

  // Poll while running OR while still streaming experiments (ready but
  // done < total). Stop once done == total.
  useEffect(() => {
    if (!teaser) return;
    const total = teaser?.data?.total;
    const done  = teaser?.data?.done;
    const streaming = typeof total === "number" && typeof done === "number" && done < total;
    const needsPoll = teaser.state === "running" || streaming;
    if (!needsPoll) {
      if (teaserPoll.current) { clearInterval(teaserPoll.current); teaserPoll.current = null; }
      return;
    }
    teaserPoll.current = setInterval(async () => {
      const resp = await api.getTeaser();
      if (resp.ok) setTeaser(resp.data);
    }, 3000);
    return () => teaserPoll.current && clearInterval(teaserPoll.current);
  }, [teaser?.state, teaser?.data?.done, teaser?.data?.total]);

  const handleShowPoints = async () => {
    if (!selectedSite) return;
    setTeaser({ state: "running", site_url: selectedSite });
    const resp = await api.createTeaser(selectedSite);
    if (resp.ok) setTeaser(resp.data);
    else setTeaser({ state: "failed", error: resp.data?.error || "Could not start preview." });
  };

  const cancelled = params.get("cancelled") === "1";

  const handlePay = async () => {
    if (!selectedSite) {
      setCheckoutError("Please pick a site to scan.");
      return;
    }
    setCheckoutError("");
    setCheckoutLoading(true);
    const resp = await api.createCheckout({ site_url: selectedSite });
    setCheckoutLoading(false);
    const url = resp?.data?.url;
    if (resp.ok && typeof url === "string" && /^https:\/\/(buy|checkout)\.stripe\.com\//.test(url)) {
      window.location.href = url;
    } else {
      console.error("[Checkout] unexpected response", resp?.status, resp?.data);
      setCheckoutError(
        resp?.data?.error ||
          `Could not start checkout (status ${resp?.status ?? "network error"}). Check STRIPE_PAYMENT_LINK in config/application.yml.`
      );
    }
  };

  const noSites = sitesLoaded && sites.length === 0 && !sitesError;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 py-10 grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8 items-start">
        {/* Main column */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 text-lg">✓</span>
            <h1 className="text-xl font-extrabold text-slate-900">
              Your Google account is connected
            </h1>
          </div>

          {user?.email && (
            <p className="text-sm text-slate-500 mb-6">
              Signed in as <span className="font-semibold text-slate-900">{user.email}</span>. Read-only Search Console access granted.
            </p>
          )}

          {/* Site picker */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Pick the site to scan
            </label>
            <p className="text-xs text-slate-500 mb-3">
              These are the properties your Google account has access to in Search Console.
            </p>

            {sitesLoading && (
              <div className="text-sm text-slate-500">Loading your Search Console sites…</div>
            )}

            {sitesError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {sitesError}
              </div>
            )}

            {noSites && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3">
                We didn&apos;t find any verified Search Console properties on this Google account.
                <br />
                <a
                  href="https://search.google.com/search-console/welcome"
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-semibold"
                >
                  Add and verify your site in Search Console
                </a>
                , then come back and refresh.
              </div>
            )}

            {sortedSites.length > 0 && (
              <div className="relative">
                <select
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  className="w-full appearance-none text-sm text-slate-900 bg-white border border-slate-200 rounded-lg pl-3 pr-9 py-2.5 cursor-pointer hover:border-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                >
                  {sortedSites.map((s) => {
                    const levelMap = {
                      siteOwner: "Owner",
                      siteFullUser: "Full access",
                      siteRestrictedUser: "Restricted",
                    };
                    const levelLabel = levelMap[s.permission_level] || s.permission_level;
                    return (
                      <option key={s.site_url} value={s.site_url}>
                        {formatSiteLabel(s.site_url)} · {levelLabel}
                      </option>
                    );
                  })}
                </select>
                <svg
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"
                >
                  <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            )}
          </div>

          {/* Live teaser insights — once ready, render here in main column */}
          {teaser?.state === "ready" && (
            <TeaserInsights teaser={teaser} />
          )}

          {/* Static example — shown only before any real teaser exists.
              Hidden the moment the user's own teaser starts producing cards,
              or once a previous teaser already generated experiments. */}
          {(() => {
            const teaserExpCount =
              (teaser?.data?.experiments?.length || teaser?.data?.insights?.length) || 0;
            const showSample =
              teaserExpCount === 0 && teaser?.state !== "ready";
            if (!showSample) return null;
            return (
            <div className="mb-2" id="sample-insights">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                What your report will look like
              </div>
              <p className="text-sm text-slate-500 mb-4">
                One real experiment from a past scan. Your report contains 150+ of these — same format, tailored to your site&apos;s data.
              </p>
              <ExperimentCard exp={sampleExperiment} />
            </div>
            );
          })()}
        </div>

        {/* Sticky payment panel */}
        <aside className="lg:sticky lg:top-6 space-y-4">
          {/* Free preview — lives in the same panel as the pay button */}
          <TeaserPanel
            selectedSite={selectedSite}
            formatSiteLabel={formatSiteLabel}
            teaser={teaser}
            onShowPoints={handleShowPoints}
          />

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-sm font-semibold text-slate-500">One-time payment</span>
            </div>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-4xl font-extrabold text-slate-900">$49</span>
              <span className="text-sm text-slate-500">· no subscription</span>
            </div>

            <div className="text-xs text-slate-500 space-y-2 mb-5">
              <div className="flex items-start gap-2">
                <span className="text-emerald-500">✓</span>
                <span>20+ scored experiments, ranked by impact</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-emerald-500">✓</span>
                <span>Exact title &amp; meta rewrites, ready to paste</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-emerald-500">✓</span>
                <span>A Claude prompt per fix to ship in your repo</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-emerald-500">✓</span>
                <span>Delivered within 24 hours · refund anytime</span>
              </div>
            </div>

            <button
              onClick={handlePay}
              disabled={checkoutLoading || !selectedSite}
              className="w-full px-5 py-3 rounded-lg text-sm font-extrabold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer transition-colors shadow-sm"
            >
              {checkoutLoading ? "Redirecting to Stripe…" : "Pay $49 & start my scan →"}
            </button>

            {selectedSite && (
              <p className="text-xs text-slate-500 mt-3 break-all">
                Scanning <span className="font-semibold text-slate-700">{formatSiteLabel(selectedSite)}</span>
              </p>
            )}

            {cancelled && (
              <p className="text-xs text-amber-600 mt-3">
                Payment cancelled — you can try again anytime.
              </p>
            )}

            {checkoutError && <p className="text-xs text-red-600 mt-3">{checkoutError}</p>}

            <p className="text-[10px] text-slate-400 text-center mt-4">
              Secure checkout powered by Stripe.
            </p>

            <button
              onClick={() => navigate("/start")}
              className="w-full mt-4 text-[11px] text-slate-400 hover:text-slate-600 cursor-pointer"
            >
              Not you? Use a different Google account.
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
